import { BAND_LABEL, SCORE_MAX, SCORE_MIN, type PublicBadge } from '@cyberscore/contracts';
import { BadgeCheck, Link2Off, ShieldCheck } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { fetchPublicBadge } from './badge';
import { BadgeCard } from './ShareScore';
import './business.css';

type State = { kind: 'loading' } | { kind: 'shown'; badge: PublicBadge } | { kind: 'gone' } | { kind: 'error'; message: string }; // prettier-ignore

/**
 * A business's public badge page (decision 0032), for anyone with the link: no sign-in. It shows the
 * business's name, its latest complete CyberScore, the band and how much of it is verified, and nothing
 * else. A link the business stopped sharing shows that it isn't shared.
 */
export function PublicBadgePage({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ['public-badge', token],
    queryFn: () => fetchPublicBadge(token),
    retry: false,
  });
  const state: State = q.isPending
    ? { kind: 'loading' }
    : q.isError
      ? { kind: 'error', message: q.error.message }
      : q.data
        ? { kind: 'shown', badge: q.data }
        : { kind: 'gone' };
  const title = state.kind === 'shown' ? `${state.badge.business} · CyberScore ${state.badge.score}` : null;

  // The tab names the business while its badge shows, and goes back to the app's name after.
  useEffect(() => {
    if (!title) return;
    const before = document.title;
    document.title = title;
    return () => {
      document.title = before;
    };
  }, [title]);

  return (
    <div className="biz public-badge">
      <main className="pb-main">
        <div className="pb-brand">
          <span className="brand-mark" aria-hidden>
            <ShieldCheck size={18} />
          </span>
          <b>CyberScore</b>
        </div>
        {state.kind === 'loading' ? (
          <section className="bcard pb-card">
            <p className="muted">Loading…</p>
          </section>
        ) : state.kind === 'shown' ? (
          <section className="bcard pb-card" aria-label={`CyberScore for ${state.badge.business}`}>
            <BadgeCard
              business={state.badge.business}
              score={state.badge.score}
              band={state.badge.band}
              verified={state.badge.verified}
              date={state.badge.scored_at}
            />
            <ul className="pb-facts">
              <li>
                <BadgeCheck size={16} /> {state.badge.business} shares this score, and it updates as their
                security does.
              </li>
              <li>
                <BadgeCheck size={16} /> A CyberScore runs from {SCORE_MIN} to {SCORE_MAX}.{' '}
                {state.badge.score} is in the {BAND_LABEL[state.badge.band]} band.
              </li>
              <li>
                <BadgeCheck size={16} /> {Math.round(state.badge.verified * 100)}% of it is verified from
                evidence or connected systems; the rest is what the business declared.
              </li>
            </ul>
          </section>
        ) : (
          <section className="bcard pb-card empty-card">
            <span className="hicon big">
              <Link2Off size={22} />
            </span>
            <h2>
              {state.kind === 'gone' ? 'This badge link is not shared' : 'The badge could not be loaded'}
            </h2>
            <p className="muted">
              {state.kind === 'gone'
                ? 'The business may have stopped sharing its score, or the link is mistyped. Ask them for a new link.'
                : state.message}
            </p>
          </section>
        )}
        <p className="muted pb-foot">
          CyberScore measures how well a business is protected against the cyber losses insurers see most.
        </p>
      </main>
    </div>
  );
}
